import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { ArrowRight, ChevronRight, AlertCircle } from "lucide-react";

const TOC = [
  { id: "quick-answer", label: "Quick Compatibility Answer" },
  { id: "comparison", label: "EcoFlow-Compatible Comparison" },
  { id: "specs", label: "Verified EcoFlow Input Specs" },
  { id: "river-pro", label: "RIVER Pro vs RIVER 2 Pro" },
  { id: "connectors", label: "Connectors & Adapters" },
  { id: "reviews", label: "Compatible Product Cards" },
  { id: "sources", label: "Primary Sources" },
  { id: "faq", label: "FAQ" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "EcoFlow Solar Panel Compatibility Guide: RIVER Pro, RIVER 2 Pro & DELTA 2",
  "description": "Match solar panels to EcoFlow RIVER Pro, RIVER 2, RIVER 2 Pro, DELTA 2, DELTA 2 Max, and DELTA Pro using verified PV input limits.",
  "url": "https://highestefficiencysolarcell.com/solar-panel-for-ecoflow",
  "dateModified": "2026-10-09",
  "author": { "@type": "Organization", "name": "Highest Efficiency Solar Cell Editorial Team" },
  "citation": [
    "https://websiteoss.ecoflow.com/cms/upload/2022/6/17/EcoFlow%20RIVER%20Pro-User%20Manual-V1.0%EF%BC%88EN%EF%BC%89_1655456023284.pdf",
    "https://us.ecoflow.com/products/river-2-pro-portable-power-station",
    "https://manuals.ecoflow.com/us/product/delta-2-portable-power-station?lang=en_US",
    "https://us.ecoflow.com/products/delta-2-max-portable-power-station",
    "https://www.ecoflow.com/us/blog/ecoflow-delta-pro-faq-everything-you-need-to-know"
  ]
};

const ECOFLOW_SPECS = [
  { model: "EcoFlow RIVER Pro", voltage: "10–25V", current: "12A max", maxWatts: "200W max", connector: "Solar-to-XT60/XT60i cable" },
  { model: "EcoFlow RIVER 2", voltage: "11–30V", current: "8A max", maxWatts: "110W max", connector: "Solar-to-XT60/XT60i cable" },
  { model: "EcoFlow RIVER 2 Max", voltage: "11–50V", current: "13A max", maxWatts: "220W max", connector: "Solar-to-XT60/XT60i cable" },
  { model: "EcoFlow RIVER 2 Pro", voltage: "11–50V", current: "13A max", maxWatts: "220W max", connector: "Solar-to-XT60/XT60i cable" },
  { model: "EcoFlow DELTA 2", voltage: "11–60V", current: "15A max", maxWatts: "500W max", connector: "Solar-to-XT60/XT60i cable" },
  { model: "EcoFlow DELTA 2 Max", voltage: "11–60V", current: "15A per solar input", maxWatts: "500W per port / 1,000W dual", connector: "Solar-to-XT60/XT60i cable" },
  { model: "EcoFlow DELTA Pro", voltage: "11–150V", current: "15A max", maxWatts: "1,600W max", connector: "Solar-to-XT60/XT60i cable" },
];

export default function SolarPanelForEcoFlow() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const ecoflowProducts = products.filter(
    (p) =>
      p.compatible_generators?.toLowerCase().includes("ecoflow") ||
      p.product_name?.toLowerCase().includes("ecoflow")
  );

  const displayProducts = ecoflowProducts;

  return (
    <>
      <SEOHead
        title="EcoFlow Solar Panel Guide 2026 | RIVER Pro, RIVER 2 Pro & DELTA 2"
        description="Best solar panel for EcoFlow RIVER Pro, RIVER 2 Pro and DELTA 2? Compare verified PV voltage, current, wattage and connector limits before you buy."
        canonical="https://highestefficiencysolarcell.com/solar-panel-for-ecoflow"
        schema={SCHEMA}
      />
      <div className="pb-28 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">Solar Panels for EcoFlow</span>
          </nav>

          <div className="max-w-4xl mb-2">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
              EcoFlow Solar Panel Compatibility: RIVER Pro, RIVER 2 Pro & DELTA 2
            </h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-3xl">
              Looking for the best solar panel for an EcoFlow RIVER Pro, RIVER 2 Pro, RIVER 2 Max, DELTA 2, or DELTA Pro?
              Start with the station's PV input limits, not the logo on the panel. This guide compares verified EcoFlow
              voltage, current, and wattage limits and shows how to check a third-party panel before connecting it.
            </p>

            <AffiliateDisclosure />
            <TableOfContents items={TOC} />
          </div>

          <section id="quick-answer" className="mb-10 max-w-4xl rounded-2xl border border-accent/30 bg-accent/5 p-5 lg:p-6">
            <h2 className="font-heading font-bold text-xl mb-2">Quick answer: which solar panel fits my EcoFlow?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Match the panel or array to the exact EcoFlow model's PV input voltage, current, and wattage limits.
              The original <strong className="text-foreground">RIVER Pro</strong> is not the same as the
              <strong className="text-foreground"> RIVER 2 Pro</strong>: EcoFlow lists the original RIVER Pro at
              10–25V, 12A, 200W max solar input, while the RIVER 2 Pro is listed at 11–50V, 13A, 220W max.
              DELTA 2 is listed at 11–60V, 15A, 500W max. Check panel Voc, Isc/Imp, wiring, connector and polarity before connecting.
            </p>
          </section>

          <section id="comparison" className="mb-8">
            <h2 className="font-heading font-bold text-3xl mb-4">EcoFlow-Compatible Solar Panels</h2>
          </section>

          {displayProducts.length > 0 && (
            <>
              <ComparisonTable products={displayProducts} />
              <div className="mt-16" id="reviews">
                <h2 className="font-heading font-bold text-3xl mb-8">Recommended Solar Panels for EcoFlow</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            </>
          )}

          <section id="specs" className="mt-16 max-w-4xl">
            <h2 className="font-heading font-bold text-2xl mb-4">Verified EcoFlow Solar Input Specifications</h2>
            <p className="text-muted-foreground mb-6">
              These limits were checked against current EcoFlow product pages or manuals. Voltage is the first hard safety limit,
              but current matters too. A physically compatible adapter does not make an electrically incompatible array safe.
              Recheck the manual for your exact model and revision before wiring panels.
            </p>
            <div className="rounded-2xl border border-border overflow-hidden shadow-lg">
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[600px] border-collapse">
                  <thead>
                    <tr className="bg-foreground text-background">
                      <th className="py-3 px-4 text-left text-[10px] font-mono uppercase tracking-widest">EcoFlow Model</th>
                      <th className="py-3 px-4 text-center text-[10px] font-mono uppercase tracking-widest">Voltage Range</th>
                      <th className="py-3 px-4 text-center text-[10px] font-mono uppercase tracking-widest">Current Limit</th>
                      <th className="py-3 px-4 text-center text-[10px] font-mono uppercase tracking-widest">Max Solar Input</th>
                      <th className="py-3 px-4 text-center text-[10px] font-mono uppercase tracking-widest">Connector</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {ECOFLOW_SPECS.map((spec) => (
                      <tr key={spec.model} className="hover:bg-accent/5 transition-colors">
                        <td className="py-3 px-4 font-heading font-semibold text-sm">{spec.model}</td>
                        <td className="py-3 px-4 text-center text-sm font-mono">{spec.voltage}</td>
                        <td className="py-3 px-4 text-center text-sm font-mono">{spec.current}</td>
                        <td className="py-3 px-4 text-center text-sm font-mono">{spec.maxWatts}</td>
                        <td className="py-3 px-4 text-center text-sm">{spec.connector}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 italic">
              Manufacturer specifications may change. Always verify voltage range and connector type for your
              specific EcoFlow model before connecting a solar panel.
            </p>
          </section>

          <section id="river-pro" className="mt-12 max-w-4xl">
            <h2 className="font-heading font-bold text-2xl mb-4">EcoFlow RIVER Pro vs RIVER 2 Pro: do not mix up the limits</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Searchers often use “RIVER Pro” and “RIVER 2 Pro” interchangeably, but EcoFlow gives them different PV input ranges.
                The original RIVER Pro manual lists 10–25V, 12A and 200W max solar input. The newer RIVER 2 Pro product specification
                lists 11–50V, 13A and 220W max. That wider voltage window can change which panels or series/parallel arrangements are acceptable.
              </p>
              <p>
                Before buying a third-party panel, read the panel label for Voc, Vmp, Isc and Imp. If you are combining panels,
                calculate the resulting array voltage and current for the chosen wiring method. Do not size an array from wattage alone,
                and do not rely on a product title that merely says “EcoFlow compatible.”
              </p>
            </div>
          </section>

          <section id="connectors" className="mt-12 max-w-4xl">
            <h2 className="font-heading font-bold text-2xl mb-4">Connectors & Adapters</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                <strong className="text-foreground">Many third-party solar panels use MC4-style connectors.</strong>
                EcoFlow uses XT60 or XT60i solar-input cabling on many RIVER and DELTA models. Confirm the exact cable
                specified for your station. An MC4-to-XT60/XT60i adapter changes the physical connection only; it does
                not correct excessive voltage, current, reversed polarity, or an unsafe series/parallel configuration.
              </p>
              <div className="flex items-start gap-2.5 px-5 py-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/30 rounded-xl">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-400">
                  <strong>Critical:</strong> Never exceed your EcoFlow's maximum solar input voltage. Connecting
                  a panel array with a combined Voc above the station's limit can permanently damage the unit.
                  Check your panel's Voc on its spec label and compare it to the table above before connecting.
                </p>
              </div>
              <p>
                <strong className="text-foreground">Series vs parallel for EcoFlow.</strong> With matched panels,
                series wiring adds voltage while parallel wiring adds current. Neither method is automatically correct for
                a RIVER or DELTA model. Calculate the array's combined Voc and current and compare both with the exact
                station's limits. Cold weather can raise Voc, so series strings deserve additional margin based on the panel's
                temperature coefficient and manufacturer guidance.
              </p>
            </div>
          </section>

          <section id="sources" className="mt-12 max-w-4xl rounded-2xl border border-border bg-secondary/20 p-5 lg:p-6">
            <h2 className="font-heading font-bold text-xl mb-3">Primary EcoFlow sources used for this compatibility table</h2>
            <ul className="space-y-2 text-sm">
              <li><a className="text-accent-text hover:underline" href="https://websiteoss.ecoflow.com/cms/upload/2022/6/17/EcoFlow%20RIVER%20Pro-User%20Manual-V1.0%EF%BC%88EN%EF%BC%89_1655456023284.pdf" target="_blank" rel="noopener noreferrer">EcoFlow RIVER Pro user manual</a></li>
              <li><a className="text-accent-text hover:underline" href="https://us.ecoflow.com/products/river-2-pro-portable-power-station" target="_blank" rel="noopener noreferrer">EcoFlow RIVER 2 Pro specifications</a></li>
              <li><a className="text-accent-text hover:underline" href="https://manuals.ecoflow.com/us/product/delta-2-portable-power-station?lang=en_US" target="_blank" rel="noopener noreferrer">EcoFlow DELTA 2 user manual</a></li>
              <li><a className="text-accent-text hover:underline" href="https://us.ecoflow.com/products/delta-2-max-portable-power-station" target="_blank" rel="noopener noreferrer">EcoFlow DELTA 2 Max specifications</a></li>
              <li><a className="text-accent-text hover:underline" href="https://www.ecoflow.com/us/blog/ecoflow-delta-pro-faq-everything-you-need-to-know" target="_blank" rel="noopener noreferrer">EcoFlow DELTA Pro solar-input guidance</a></li>
            </ul>
            <p className="text-xs text-muted-foreground mt-3">Specifications can change by model revision or market. Verify the current manual for your exact unit before connecting an array.</p>
          </section>

          <div className="mt-12 max-w-3xl" id="faq">
            <h2 className="font-heading font-bold text-2xl mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: "Can I use any solar panel with my EcoFlow?", a: "No. The panel or array must stay inside the exact EcoFlow model's PV voltage, current and power limits, and the connector and polarity must match. A third-party panel can be compatible without being EcoFlow-branded, but compatibility has to be checked from the electrical specifications." },
                { q: "What solar panel fits the original EcoFlow RIVER Pro?", a: "EcoFlow's RIVER Pro manual lists 10–25V, 12A and 200W max solar input. Choose a panel or parallel array whose electrical specifications remain inside those limits, and use the cable specified for the RIVER Pro. Do not confuse this model with RIVER 2 Pro." },
                { q: "What solar panel fits EcoFlow RIVER 2 Pro?", a: "EcoFlow lists RIVER 2 Pro solar input at 11–50V, 13A and 220W max. Compare a candidate panel's Voc, Vmp, Isc and Imp with those limits before purchase." },
                { q: "How much solar input does EcoFlow DELTA 2 accept?", a: "EcoFlow lists DELTA 2 solar charging at 11–60V, 15A and 500W max. If you combine panels, calculate both the resulting voltage and current rather than adding wattage alone." },
                { q: "Can I connect two solar panels to one EcoFlow station?", a: "Sometimes. Series wiring raises voltage and parallel wiring raises current. The combined array must remain within the exact station's voltage, current and power limits, including cold-weather Voc considerations." },
              ].map((faq, i) => (
                <div key={i} className="border border-border rounded-xl p-5">
                  <h3 className="font-heading font-semibold mb-2 text-foreground text-base">{faq.q}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/best-portable-solar-panels"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
            >
              Compare Portable Panels <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/best-solar-panels-for-solar-generators"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
            >
              Solar Generator Panels Guide
            </Link>
          </div>
        </div>
        <EmailCapture source="ecoflow-panels" />
      </div>
    </>
  );
}